import { Box, Button, FormControl, FormLabel, Stack, Text, Textarea } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ReplyReach, useAskMarketDialogueQuestionMutation, type SupplierQuestionDto } from "../../../autogen/bff-api";
import { useApiError } from "../../../common/errors/useApiError";
import { TextLengthHint } from "../../../common/input/TextLengthHint";
import { useDisplayDateWithTime } from "../../../common/formatting/useDisplayDateWithTime";

export const MAX_SUPPLIER_QUESTION_LENGTH = 4000;

interface Props {
  marketDialogueId: string;
  questions: SupplierQuestionDto[];
  canAsk: boolean;
  onAsked: () => void;
}

export const SupplierQuestions = ({ marketDialogueId, questions, canAsk, onAsked }: Props) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const displayDateWithTime = useDisplayDateWithTime();
  const [text, setText] = useState("");
  const [ask, { isLoading }] = useAskMarketDialogueQuestionMutation();

  const send = async () => {
    try {
      await ask({ marketDialogueId, askSupplierQuestionRequest: { text } }).unwrap();
      setText("");
      onAsked();
    } catch (e) {
      trigger(e);
    }
  };

  return (
    <Stack spacing="4" data-testid="supplier-questions">
      {questions.length === 0 && (
        <Text fontSize="sm" color="smMuted">
          {t("You have asked the buyer nothing yet.")}
        </Text>
      )}
      {questions.map((question) => (
        <Box key={question.id} borderWidth="1px" borderColor="smBorder" borderRadius="md" p="3">
          <Text fontSize="xs" color="smMuted">
            {t("Asked {{time}}", { time: displayDateWithTime(question.askedAt) })}
          </Text>
          <Text whiteSpace="pre-wrap">{question.text}</Text>
          {question.reply ? (
            <Box mt="2" pl="3" borderLeftWidth="3px" borderColor="smBorder">
              <Text fontSize="xs" color="smMuted">
                {question.reply.reach === ReplyReach.AllParticipants
                  ? t("The buyer answered every participant, without your name")
                  : t("The buyer answered you")}
              </Text>
              <Text whiteSpace="pre-wrap" fontSize="sm">
                {question.reply.text}
              </Text>
            </Box>
          ) : (
            <Text mt="2" fontSize="sm" color="smMuted">
              {t("Not answered yet")}
            </Text>
          )}
        </Box>
      ))}
      {canAsk && (
        <FormControl>
          <FormLabel fontSize="sm">{t("Ask the buyer a question")}</FormLabel>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={MAX_SUPPLIER_QUESTION_LENGTH}
            rows={3}
          />
          <TextLengthHint text={text} max={MAX_SUPPLIER_QUESTION_LENGTH} />
          <Text fontSize="xs" color="smMuted" mt="1">
            {t("Only the buyer sees your question. If the buyer answers every participant, your name is left out.")}
          </Text>
          <Button
            mt="2"
            colorScheme="teal"
            onClick={() => void send()}
            isLoading={isLoading}
            isDisabled={text.trim().length === 0}
          >
            {t("Send question")}
          </Button>
        </FormControl>
      )}
    </Stack>
  );
};
